import { useMemo, useState } from "react";

type ZipCodeStepProps = {
  zipOptions: string[];
  value?: string;
  sectionTitle?: string;
  title?: string;
  description?: string;
  onNext: (zip: string) => void;
};

export function ZipCodeStep({
  zipOptions,
  value = "",
  sectionTitle = "Service Area Check",
  title = "What is your ZIP code?",
  description = "Enter your ZIP code to ensure your home is in our service area",
  onNext,
}: ZipCodeStepProps) {
  const [input, setInput] = useState(value.replace(/\D/g, ""));
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  // normalize ZIPs from API
  const normalizedZipOptions = useMemo(() => {
    return zipOptions
      .map((z) => z.replace(/\D/g, ""))
      .filter((z) => z.length === 5);
  }, [zipOptions]);

  const suggestions = useMemo(() => {
    if (input.length < 1) return [];
    if (input.length === 5) return [];
    return normalizedZipOptions.filter((z) => z.startsWith(input)).slice(0, 6);
  }, [input, normalizedZipOptions]);

  const isServiceAreaZip = normalizedZipOptions.includes(input);
  const canProceed = input.length === 5 && isServiceAreaZip;
  const showOutOfAreaMessage = input.length === 5 && !isServiceAreaZip;

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
    }

    if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      setInput(suggestions[activeIndex]);
      setActiveIndex(-1);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-center">
        <img
          src="/icons/service-truck.svg"
          alt="Service truck"
          className="h-24 w-auto sm:h-28"
        />
      </div>

      <header className="space-y-3">
        <div className="rounded-xl border border-[var(--line)] bg-[var(--brand-soft)]/25 px-3 py-2">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--brand)]">
            {sectionTitle}
          </p>
        </div>
        <div className="space-y-1">
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
          {title}
        </h2>
        <p className="text-sm text-[var(--text-muted)]">
          {description}
        </p>
        </div>
      </header>

      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          maxLength={5}
          value={input}
          onChange={(e) => {
            setInput(e.target.value.replace(/\D/g, ""));
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          placeholder="ZIP code"
          className="
            w-full rounded-2xl border border-[var(--line)] bg-white
            px-4 py-3 text-lg shadow-sm
            focus:outline-none
            focus:ring-2 focus:ring-[var(--brand)]
          "
        />

        {suggestions.length > 0 && (
          <div
            className="
              absolute z-10 mt-1 w-full
              rounded-xl border bg-white shadow
              border-[var(--line)]
              overflow-hidden
            "
          >
            {suggestions.map((zip, index) => (
              <button
                key={zip}
                type="button"
                onClick={() => {
                  setInput(zip);
                  setActiveIndex(-1);
                }}
                className={`
                  block w-full px-4 py-2 text-left
                  ${
                    index === activeIndex
                      ? "bg-[var(--brand-soft)] text-[var(--brand)]"
                      : "hover:bg-gray-50"
                  }
                `}
              >
                {zip}
              </button>
            ))}
          </div>
        )}
      </div>

      {showOutOfAreaMessage && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Sorry, you are not in our Service Area. Please contact our team.
        </p>
      )}

      <button
        disabled={!canProceed}
        onClick={() => onNext(input)}
        className={`
          w-full rounded-xl py-3 font-semibold text-white
          transition
          ${
            canProceed
              ? "bg-[var(--brand)] hover:bg-[var(--brand-dark)]"
              : "bg-gray-300 cursor-not-allowed"
          }
        `}
      >
        Next
      </button>
    </div>
  );
}
